import { FunctionComponent } from 'react';
import {
  MediaCountsBadge,
  MediaCountsBadgeV2,
  MediaShowcaseBrickContainer,
  MediaShowcaseContentContainer,
  SearchByImageWrapper,
} from './MediaShowcaseBrick.styled';
import { ScreenReaderTitle } from '../../../app-kit/helpers/screenReaderOnly';
import { MediaCount, MediaCountButtons } from '../MediaCountButtons/MediaCountButtons';
import { useMediaGalleryModalContext } from '../../contexts/MediaGalleryModalContext/MediaGalleryModalContext';
import {
  MediaShowcaseV2AerialItem,
  MediaShowcaseV2ListingItem,
} from '../../../app-kit/components/Image/types';
import { ShowcaseItem } from './subcomponents/ShowcaseItem/ShowcaseItem';
import { MediaShowcaseSplit } from './subcomponents/MediaShowcaseSplit/MediaShowcaseSplit';
import { useFeatureRollout } from '@rea-argonaut/experiment';
import { MediaCountButtonsV2 } from '../MediaCountButtonsV2/MediaCountButtonsV2';
import { SearchByImageFeatureButton } from '../../../components/SearchByImageButton/SearchByImageButton';

export interface SplitItems {
  __typename: 'PropertyPage_MediaShowcaseV2_SplitItems';
  main: MediaShowcaseV2ListingItem;
  secondary: MediaShowcaseV2ListingItem | MediaShowcaseV2AerialItem;
  tertiary: MediaShowcaseV2ListingItem;
}

export type SingleItem = MediaShowcaseV2ListingItem | MediaShowcaseV2AerialItem;

export const isMediaShowcaseSplitItems = (items: SplitItems | SingleItem): items is SplitItems => {
  return items.__typename === 'PropertyPage_MediaShowcaseV2_SplitItems';
};

interface MediaShowcaseBrick {
  mediaCounts: MediaCount[];
  items: SplitItems | SingleItem;
}

export const MediaShowcaseBrick: FunctionComponent<MediaShowcaseBrick> = ({
  mediaCounts,
  items,
}) => {
  const isMediaGalleryUplift = useFeatureRollout('media_gallery_uplift').isEnabled();
  const isMediaShowcaseUplift = useFeatureRollout('media_showcase_uplift').isEnabled();

  const { showModal } = useMediaGalleryModalContext();
  const onMediaCountClick = (imageIndexFrom: number) =>
    showModal({
      openAction: 'MediaShowcaseBrick:Click',
      initialImageIndex: imageIndexFrom ?? 0,
    });
  const isSplitItems = isMediaShowcaseSplitItems(items);
  const searchByImageButton = isSplitItems
    ? items.main.searchByImageButton
    : 'searchByImageButton' in items
      ? items.searchByImageButton
      : null;

  return (
    <MediaShowcaseBrickContainer as="section">
      <ScreenReaderTitle as="p">Property images</ScreenReaderTitle>
      {isMediaGalleryUplift || isMediaShowcaseUplift ? (
        <>
          <MediaCountsBadgeV2>
            <MediaCountButtonsV2 counts={mediaCounts} onClickHandler={onMediaCountClick} />
          </MediaCountsBadgeV2>
          {searchByImageButton && (
            <SearchByImageWrapper $isSplitItem={isSplitItems}>
              <SearchByImageFeatureButton searchByImageButton={searchByImageButton} />
            </SearchByImageWrapper>
          )}
        </>
      ) : (
        <MediaCountsBadge>
          <MediaCountButtons counts={mediaCounts} onClickHandler={onMediaCountClick} />
        </MediaCountsBadge>
      )}

      <MediaShowcaseContentContainer>
        {isSplitItems ? <MediaShowcaseSplit items={items} /> : <ShowcaseItem item={items} />}
      </MediaShowcaseContentContainer>
    </MediaShowcaseBrickContainer>
  );
};
